import { useState, useEffect } from 'react'
import {
  useGetCalendarSharingSettingsQuery,
  useUpdateCalendarSharingSettingsMutation,
} from '../../../../api/serverStateHooks'
import { appToast } from '../../../../components/AppToast'

const SHARING_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    desc: 'Hidden from team & all scopes',
    icon: 'lock',
  },
  {
    value: 'free_busy',
    label: 'Free/Busy',
    desc: 'Shows slot as "Busy" (default)',
    icon: 'visibility_off',
  },
  {
    value: 'full',
    label: 'Full Details',
    desc: 'Shows event names & tasks',
    icon: 'visibility',
  },
]

export default function CalendarSharingSettings() {
  const { data, isLoading } = useGetCalendarSharingSettingsQuery()
  const [updateSettings, { isLoading: isUpdating }] = useUpdateCalendarSharingSettingsMutation()
  const [currentVal, setCurrentVal] = useState('free_busy')

  useEffect(() => {
    if (data?.visibility) {
      setCurrentVal(data.visibility)
    }
  }, [data?.visibility])

  async function handleSelect(val) {
    if (val === currentVal) return
    setCurrentVal(val)
    try {
      await updateSettings({ visibility: val }).unwrap()
      appToast.success('Calendar sharing visibility updated')
    } catch {
      appToast.error('Failed to update calendar sharing visibility')
      if (data?.visibility) {
        setCurrentVal(data.visibility)
      }
    }
  }

  return (
    <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low/80 p-3.5 shadow-xs">
      <div className="mb-2.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
          <span className="material-symbols-outlined text-sm text-primary">share</span>
          <span>My Calendar Sharing</span>
        </div>
        {isUpdating ? (
          <span className="material-symbols-outlined animate-spin text-xs text-primary">
            progress_activity
          </span>
        ) : null}
      </div>

      <p className="mb-3 text-[11px] leading-relaxed text-on-surface-variant">
        Controls how your scheduled jobs & meetings appear to colleagues on Team and All views.
      </p>

      <div className="space-y-1.5">
        {SHARING_OPTIONS.map((opt) => {
          const isSelected = currentVal === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              disabled={isLoading || isUpdating}
              onClick={() => handleSelect(opt.value)}
              className={`flex w-full items-start gap-2.5 rounded-xl border p-2 text-left transition-all ${
                isSelected
                  ? 'border-primary/50 bg-primary/10 shadow-xs ring-1 ring-primary/30'
                  : 'border-transparent bg-surface-container-lowest/70 hover:border-outline-variant/30 hover:bg-surface-container-lowest'
              }`}
            >
              <span
                className={`material-symbols-outlined mt-0.5 text-base ${
                  isSelected ? 'text-primary font-semibold' : 'text-on-surface-variant'
                }`}
              >
                {opt.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-bold leading-tight ${
                      isSelected ? 'text-primary' : 'text-on-surface'
                    }`}
                  >
                    {opt.label}
                  </span>
                  {isSelected ? (
                    <span className="material-symbols-outlined text-xs text-primary">check_circle</span>
                  ) : null}
                </div>
                <p className="text-[10px] text-on-surface-variant mt-0.5 truncate">{opt.desc}</p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
